import { FC, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { RootState } from 'store';
import { StyleSpecifications } from 'order/wizard/orderStyles/interface/StyleSpecifications';
import { Wrapper } from 'shared/components/Wrapper';
import { Spacer } from 'shared/components/Layout';

const ToggleContainer = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleLabel = styled.span<{ isActive?: boolean; disabled?: boolean }>`
  font-size: 14px;
  font-weight: ${({ isActive }) => (isActive ? 600 : 400)};
  color: ${({ theme, isActive, disabled }) =>
    // eslint-disable-next-line no-nested-ternary
    disabled ? theme.silverSand : isActive ? theme.kashmirBlue : theme.nevada};
  transition: all 300ms ease;
`;

const ToggleSwitch = styled.div<{ isActive: boolean; disabled?: boolean }>`
  position: relative;
  width: 44px;
  height: 22px;
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.silverSand : theme.kashmirBlue};
  border-radius: 11px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 300ms ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${({ isActive }) => (isActive ? '24px' : '2px')};
    width: 18px;
    height: 18px;
    background-color: ${({ theme }) => theme.white};
    border-radius: 50%;
    transition: left 300ms ease;
  }
`;

interface DesignerFinishToggleProps {
  onToggle?: (isDesignerFinish: boolean) => void;
  hasDesignerFinishes?: boolean;
}

const DesignerFinishToggle: FC<DesignerFinishToggleProps> = ({
  onToggle,
  hasDesignerFinishes = false,
}) => {
  const { watch, setValue } = useFormContext<StyleSpecifications>();

  const canEdit = useSelector((state: RootState) => state.orderReducer.canEdit);

  const woodOrMaterialWatched = watch('woodOrMaterial');
  const showDesignerFinishesWatched = watch('showDesignerFinishes');
  const specialFinishSampleWatched = watch('specialFinishSample');

  const hasSpecialFinishSample = specialFinishSampleWatched?.length > 0;
  const isDisabled =
    !canEdit ||
    !woodOrMaterialWatched ||
    hasSpecialFinishSample ||
    !hasDesignerFinishes;

  useEffect(() => {
    if (hasSpecialFinishSample) {
      setValue('showDesignerFinishes', false, { shouldDirty: true });
    }
  }, [hasSpecialFinishSample, setValue]);

  const handleToggle = () => {
    if (isDisabled) return;

    const newValue = !showDesignerFinishesWatched;
    setValue('showDesignerFinishes', newValue, { shouldDirty: true });
    setValue('showAvailableFinishesOnly', newValue, { shouldDirty: true });

    if (onToggle) {
      onToggle(newValue);
    }
  };

  return (
    <>
      <Spacer h="16px" />
      <ToggleContainer>
        <ToggleLabel
          isActive={!showDesignerFinishesWatched}
          disabled={isDisabled}
        >
          Finishes
        </ToggleLabel>
        <ToggleSwitch
          isActive={!!showDesignerFinishesWatched}
          disabled={isDisabled}
          onClick={handleToggle}
          data-test="toggle-designer-finishes"
        />
        <ToggleLabel
          isActive={!!showDesignerFinishesWatched}
          disabled={isDisabled}
        >
          Designer Finishes
        </ToggleLabel>
      </ToggleContainer>
      <Spacer h="16px" />
    </>
  );
};

export default DesignerFinishToggle;
